import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ShoppingBag, Coins, Check, X, Loader2, Sparkles, Frame, Palette, IdCard, Crown, Type } from "lucide-react";

const TABS = [
  { id: "entry",          label: "Entry FX",   icon: Sparkles,  color: "#FF2A5F", desc: "Grand entrance animations for live rooms" },
  { id: "frame",          label: "Frames",     icon: Frame,     color: "#FFD700", desc: "Animated rings around your avatar" },
  { id: "theme",          label: "Themes",     icon: Palette,   color: "#A855F7", desc: "Profile & feed background themes" },
  { id: "profile_card",   label: "Profile Card", icon: IdCard,  color: "#00E5FF", desc: "Business-card style profile popover" },
  { id: "vanity_badge",   label: "Badges",     icon: Crown,     color: "#FFD700", desc: "Prestige creator badges (Diamond, Legend…)" },
  { id: "nickname_color", label: "Nickname",   icon: Type,      color: "#00E5FF", desc: "Colored gradient username in chat & comments" },
];

const RARITY_STYLES = {
  common:    { ring: "border-white/15",         chip: "bg-white/10 text-white/80"        },
  rare:      { ring: "border-[#00E5FF]/50",     chip: "bg-[#00E5FF]/15 text-[#00E5FF]"   },
  epic:      { ring: "border-[#A855F7]/50",     chip: "bg-[#A855F7]/15 text-[#A855F7]"   },
  legendary: { ring: "border-[#FF7A00]/60",     chip: "bg-[#FF7A00]/15 text-[#FF7A00]"   },
};

export default function Store() {
  const [tab, setTab] = useState("entry");
  const [items, setItems] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [inv, setInv] = useState({ owned: [], equipped: {} });
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [confirmBuy, setConfirmBuy] = useState(null);
  const [confirmBundle, setConfirmBundle] = useState(null);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState("");

  const load = () => {
    setLoading(true);
    Promise.allSettled([
      api.get(`/store/items?category=${tab}`),
      api.get(`/store/my-items`),
      api.get(`/auth/me`),
      api.get(`/store/bundles`),
    ]).then(([r1, r2, r3, r4]) => {
      if (r1.status === "fulfilled") setItems(r1.value.data || []);
      if (r2.status === "fulfilled") setInv(r2.value.data || { owned: [], equipped: {} });
      if (r3.status === "fulfilled") setMe(r3.value.data);
      if (r4.status === "fulfilled") setBundles(r4.value.data || []);
    }).finally(() => setLoading(false));
  };
  useEffect(load, [tab]); // eslint-disable-line

  const isOwned = (id) => inv.owned.some(x => x.item_id === id);
  const isEquipped = (item) => (inv.equipped?.[item.category]?.item_id === item.id);

  const purchase = async (item) => {
    setBusy(true);
    try {
      await api.post(`/store/purchase/${item.id}`);
      await api.post(`/store/equip/${item.id}`);
      setFlash(`Bought & equipped ${item.name} for ${item.price} Goldly`);
      setConfirmBuy(null); load();
      setTimeout(() => setFlash(""), 3000);
    } catch (e) {
      setFlash(e?.response?.data?.detail || "Purchase failed");
      setTimeout(() => setFlash(""), 3500);
    } finally { setBusy(false); }
  };

  const equip = async (item) => {
    try {
      await api.post(`/store/equip/${item.id}`);
      setFlash(`Equipped ${item.name}`); load();
      setTimeout(() => setFlash(""), 2000);
    } catch { /* ignore */ }
  };

  const unequip = async (item) => {
    try {
      await api.post(`/store/unequip/${item.category}`);
      setFlash(`Unequipped ${item.name}`); load();
      setTimeout(() => setFlash(""), 2000);
    } catch { /* ignore */ }
  };

  const purchaseBundle = async (b) => {
    setBusy(true);
    try {
      await api.post(`/store/bundles/${b.id}/purchase`);
      setFlash(`Bought ${b.name}! ${b.items.length} items added.`);
      setConfirmBundle(null); load();
      setTimeout(() => setFlash(""), 3000);
    } catch (e) {
      setFlash(e?.response?.data?.detail || "Purchase failed");
      setTimeout(() => setFlash(""), 3500);
    } finally { setBusy(false); }
  };

  const activeTabMeta = TABS.find(t => t.id === tab);

  return (
    <div className="px-3 py-5 md:max-w-4xl md:mx-auto" data-testid="store-page">
      <div className="flex items-center gap-2 mb-3">
        <Link to="/more" className="p-2 rounded-full bg-white/8" data-testid="store-back"><ArrowLeft className="w-4 h-4"/></Link>
        <h1 className="font-outfit font-extrabold text-3xl tracking-tighter flex items-center gap-2"><ShoppingBag className="w-6 h-6 text-[#FF7A00]"/>Store</h1>
        <div className="ml-auto flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#FF7A00]/10 border border-[#FF7A00]/30 text-[#FF7A00] text-xs font-extrabold">
          <Coins className="w-3.5 h-3.5"/>{(me?.wallet_goldly ?? 0).toLocaleString()}
        </div>
      </div>
      <p className="text-white/60 text-sm mb-4">Buy cosmetics with Goldly, equip your favourites, and stand out across live rooms, comments and your profile.</p>

      {/* Bundles — high-value deals up top */}
      {bundles.length > 0 && (
        <div className="mb-5" data-testid="store-bundles">
          <div className="text-xs uppercase tracking-wider text-[#FF7A00] font-extrabold mb-2 flex items-center gap-1.5">✨ Bundle Deals · save up to 24%</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
            {bundles.map(b => {
              const off = b.regular_price ? Math.round((b.savings / b.regular_price) * 100) : 0;
              return (
                <button key={b.id} onClick={() => setConfirmBundle(b)}
                  data-testid={`store-bundle-${b.id}`}
                  className={`text-left rounded-2xl overflow-hidden border border-white/15 active:scale-[0.98] transition bg-gradient-to-br ${b.banner_gradient || "from-[#00E5FF] to-[#A855F7]"}`}>
                  <div className="p-3 flex flex-col gap-2 bg-black/40 backdrop-blur">
                    <div className="flex items-center justify-between">
                      <div className="font-outfit font-extrabold text-sm">{b.name}</div>
                      {off > 0 && <div className="text-[10px] font-extrabold px-1.5 py-0.5 rounded-full bg-[#FF7A00] text-black">{off}% OFF</div>}
                    </div>
                    <div className="text-[11px] text-white/75 leading-snug line-clamp-2">{b.description}</div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {(b.items || []).slice(0, 4).map(it => (
                        <span key={it.id} className="text-[10px] px-1.5 py-0.5 rounded-full bg-black/40 border border-white/15">{it.name}</span>
                      ))}
                    </div>
                    <div className="flex items-center gap-2 pt-1">
                      <div className="text-lg font-extrabold text-[#FF7A00] flex items-center gap-1"><Coins className="w-4 h-4"/>{b.price.toLocaleString()}</div>
                      {b.savings > 0 && <div className="text-[11px] text-white/50 line-through">{b.regular_price.toLocaleString()}</div>}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1.5 overflow-x-auto scrollbar-hide mb-4 pb-1">
        {TABS.map(t => {
          const Ic = t.icon; const active = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              data-testid={`store-tab-${t.id}`}
              className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-extrabold border ${active ? "text-black" : "text-white/70 border-white/10"}`}
              style={active ? { background: t.color, borderColor: t.color } : {}}>
              <Ic className="w-3.5 h-3.5"/>{t.label}
            </button>
          );
        })}
      </div>
      <div className="text-[11px] text-white/50 mb-3">{activeTabMeta?.desc}</div>

      {/* Grid */}
      {loading ? (
        <div className="flex justify-center py-14"><Loader2 className="w-6 h-6 animate-spin text-[#FF7A00]"/></div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5" data-testid="store-grid">
          {items.map(item => {
            const rs = RARITY_STYLES[item.rarity] || RARITY_STYLES.common;
            const owned = isOwned(item.id);
            const equipped = isEquipped(item);
            return (
              <div key={item.id} data-testid={`store-item-${item.id}`}
                className={`relative rounded-2xl border ${rs.ring} bg-gradient-to-br from-white/5 to-black/40 p-3 flex flex-col gap-2 backdrop-blur active:scale-[0.98] transition`}>
                <div className={`text-[9px] font-extrabold uppercase tracking-wider ${rs.chip} px-1.5 py-0.5 rounded-full self-start`}>{item.rarity}</div>
                <StorePreview item={item}/>
                <div className="text-sm font-bold text-white leading-tight line-clamp-2">{item.name}</div>
                {owned ? (
                  equipped ? (
                    <button onClick={() => unequip(item)} data-testid={`store-unequip-${item.id}`}
                      className="mt-auto py-1.5 rounded-full text-[11px] font-extrabold bg-[#00E5FF] text-black flex items-center justify-center gap-1">
                      <Check className="w-3 h-3"/>Equipped
                    </button>
                  ) : (
                    <button onClick={() => equip(item)} data-testid={`store-equip-${item.id}`}
                      className="mt-auto py-1.5 rounded-full text-[11px] font-extrabold bg-white/10 border border-white/15 text-white/85">
                      Equip
                    </button>
                  )
                ) : (
                  <button onClick={() => setConfirmBuy(item)} data-testid={`store-buy-${item.id}`}
                    className="mt-auto py-1.5 rounded-full text-[11px] font-extrabold text-black flex items-center justify-center gap-1"
                    style={{ background: `linear-gradient(to right, ${activeTabMeta.color}, #FFD700)` }}>
                    <Coins className="w-3 h-3"/>{item.price}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Confirm purchase modal */}
      <AnimatePresence>
        {confirmBuy && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
            className="fixed inset-0 z-[80] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/70" onClick={() => setConfirmBuy(null)}/>
            <motion.div initial={{y:20, scale:0.95}} animate={{y:0, scale:1}} exit={{y:20, scale:0.95}}
              className="relative w-full max-w-sm rounded-3xl bg-[#0B0F19] border border-white/15 p-5">
              <button onClick={() => setConfirmBuy(null)} data-testid="store-confirm-close" className="absolute top-3 right-3 p-1.5 rounded-full bg-white/8"><X className="w-4 h-4"/></button>
              <div className="font-outfit font-extrabold text-xl mb-1">Buy {confirmBuy.name}?</div>
              <div className="text-sm text-white/60 mb-3">This item will be equipped automatically. You can unequip anytime for free.</div>
              <StorePreview item={confirmBuy} large/>
              <div className="mt-4 flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10">
                <div className="text-xs text-white/60">Cost</div>
                <div className="font-extrabold text-[#FF7A00] flex items-center gap-1"><Coins className="w-4 h-4"/>{confirmBuy.price.toLocaleString()}</div>
              </div>
              <div className="mt-2 flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10">
                <div className="text-xs text-white/60">You have</div>
                <div className="font-extrabold text-[#FF7A00] flex items-center gap-1"><Coins className="w-4 h-4"/>{(me?.wallet_goldly ?? 0).toLocaleString()}</div>
              </div>
              <button onClick={() => purchase(confirmBuy)} disabled={busy}
                data-testid="store-confirm-buy"
                className="mt-4 w-full py-2.5 rounded-full bg-gradient-to-r from-[#FF7A00] to-[#FF2A5F] text-black font-outfit font-extrabold disabled:opacity-40 active:scale-95 flex items-center justify-center gap-2">
                {busy ? <><Loader2 className="w-4 h-4 animate-spin"/>Buying…</> : <>🎉 Confirm purchase</>}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {flash && (
        <div data-testid="store-flash" className="fixed bottom-24 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-black/85 border border-white/15 text-sm font-bold shadow-lg z-[90]">
          {flash}
        </div>
      )}

      {/* Bundle confirm modal */}
      <AnimatePresence>
        {confirmBundle && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
            className="fixed inset-0 z-[80] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/70" onClick={() => setConfirmBundle(null)}/>
            <motion.div initial={{y:20, scale:0.95}} animate={{y:0, scale:1}} exit={{y:20, scale:0.95}}
              className="relative w-full max-w-sm rounded-3xl bg-[#0B0F19] border border-white/15 p-5">
              <button onClick={() => setConfirmBundle(null)} data-testid="bundle-close" className="absolute top-3 right-3 p-1.5 rounded-full bg-white/8"><X className="w-4 h-4"/></button>
              <div className="font-outfit font-extrabold text-xl mb-1">{confirmBundle.name}</div>
              <div className="text-sm text-white/60 mb-3">{confirmBundle.description}</div>
              <div className={`rounded-xl bg-gradient-to-br ${confirmBundle.banner_gradient} p-3`}>
                <div className="bg-black/50 rounded-xl p-2 space-y-1">
                  {(confirmBundle.items || []).map(it => (
                    <div key={it.id} className="flex items-center justify-between text-[11px]">
                      <span className="text-white/85">{it.name}</span>
                      <span className="text-white/50 line-through">{it.price} g</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between p-2.5 rounded-xl bg-white/5 border border-white/10">
                <div className="text-xs text-white/60">Bundle price</div>
                <div className="font-extrabold text-[#FF7A00] flex items-center gap-1"><Coins className="w-4 h-4"/>{confirmBundle.price.toLocaleString()}</div>
              </div>
              {confirmBundle.savings > 0 && (
                <div className="mt-1 flex items-center justify-between p-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs">
                  <span>You save</span>
                  <span className="font-extrabold">{confirmBundle.savings.toLocaleString()} Goldly</span>
                </div>
              )}
              <button onClick={() => purchaseBundle(confirmBundle)} disabled={busy}
                data-testid="bundle-confirm"
                className="mt-4 w-full py-2.5 rounded-full bg-gradient-to-r from-[#FF7A00] to-[#FF2A5F] text-black font-outfit font-extrabold disabled:opacity-40 active:scale-95 flex items-center justify-center gap-2">
                {busy ? <><Loader2 className="w-4 h-4 animate-spin"/>Buying bundle…</> : <>🎁 Grab this bundle</>}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/** StorePreview — visual for a store item, per category */
function StorePreview({ item, large = false }) {
  const p = item.payload || {};
  const size = large ? "h-24" : "h-16";
  const box = "w-full rounded-xl flex items-center justify-center relative overflow-hidden";
  if (item.category === "entry") {
    return (
      <div className={`${box} ${size} bg-gradient-to-br`} style={{ backgroundImage: `linear-gradient(135deg, ${(p.colors || ["#FF2A5F", "#FFD700"]).join(", ")})` }}>
        <div className="text-4xl animate-pulse">{p.icon || "✨"}</div>
      </div>
    );
  }
  if (item.category === "frame") {
    return (
      <div className={`${box} ${size} bg-black/40`}>
        <div className="w-14 h-14 rounded-full border-4" style={{ borderColor: p.color || "#FFD700", boxShadow: p.glow ? `0 0 16px ${p.color || "#FFD700"}` : "" }}>
          <img src="https://api.dicebear.com/7.x/avataaars/svg?seed=you" alt="preview" className="w-full h-full rounded-full"/>
        </div>
      </div>
    );
  }
  if (item.category === "theme") {
    return <div className={`${box} ${size} bg-gradient-to-br ${p.bg_gradient || "from-white to-black"}`}><div className="text-xs font-bold text-black">Theme</div></div>;
  }
  if (item.category === "profile_card") {
    const gradient = (p.colors || ["#00E5FF", "#A855F7"]).join(", ");
    return (
      <div className={`${box} ${size}`} style={{ background: p.style === "gradient" ? `linear-gradient(135deg, ${gradient})` : "rgba(255,255,255,0.05)", border: `1px solid ${p.border || "rgba(255,255,255,0.15)"}` }}>
        <div className="text-xs font-bold">{p.style?.toUpperCase() || "CARD"}</div>
      </div>
    );
  }
  if (item.category === "vanity_badge") {
    return (
      <div className={`${box} ${size} bg-black/40`}>
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-extrabold" style={{ background: (p.color || "#FFD700") + "22", border: `1px solid ${p.color || "#FFD700"}55`, color: p.color || "#FFD700" }}>
          <span className="text-base">{p.emoji}</span>{p.label}
        </div>
      </div>
    );
  }
  if (item.category === "nickname_color") {
    return (
      <div className={`${box} ${size} bg-black/40`}>
        <div className={`text-xl font-extrabold bg-gradient-to-r ${p.gradient} bg-clip-text text-transparent ${p.animated ? "animate-pulse" : ""}`}>@yourname</div>
      </div>
    );
  }
  return <div className={`${box} ${size} bg-white/5`}>—</div>;
}
